import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  User as UserIcon,
  Mail,
  ShieldCheck,
  Calendar,
  Watch,
  LogOut,
  KeyRound,
  Loader2,
  Check,
  AlertCircle,
  Wallet,
  Sparkles,
  Fingerprint,
  Camera,
  Archive,
  Clock,
  Heart,
  Gift,
  Pencil,
  X,
  Eye,
  EyeOff,
  Trash2,
  ChevronDown,
  Compass,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { useTimeWatchData } from "../lib/useTimeWatchData";
import { useImpronte } from "../lib/useImpronte";
import { useIstanti } from "../lib/useIstanti";
import { useBackups } from "../lib/useBackups";
import { useLocalStorageState } from "../lib/useLocalStorageState";
import { Download, Loader2 as Loader2Icon } from "lucide-react";
import NdaAcceptancesSection from "../components/nda/NdaAcceptancesSection";
import {
  convertEuroToTime,
  formatInt,
  minutesToTimeParts,
  pmvYearsMonthsDaysUntilCentenary,
} from "../lib/timeMath";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function periodLabel(period) {
  // "YYYY-MM" → "Luglio 2026"
  if (!period || period.length !== 7) return period;
  const [y, m] = period.split("-");
  const months = [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
  ];
  const idx = Math.max(0, Math.min(11, Number(m) - 1));
  return `${months[idx]} ${y}`;
}

export default function AreaPersonalePage() {
  const { user, logout, changePassword, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);

  // UI-only preference: default-open collapsible sections. Solely visual —
  // does not touch data, sync or backend calls. Persistita in localStorage
  // in una singola struttura (non contiene mai password/token/dati personali).
  const [collapsedPrefs, setCollapsedPrefs] = useLocalStorageState(
    "time-inside:personal-area:collapsed-sections",
    { account: true, attivita: true, archivio: true }
  );
  const openAccount = collapsedPrefs.account !== false;
  const openAttivita = collapsedPrefs.attivita !== false;
  const openArchivio = collapsedPrefs.archivio !== false;
  const setOpenAccount = (next) =>
    setCollapsedPrefs((prev) => ({
      ...prev,
      account: typeof next === "function" ? next(prev.account !== false) : next,
    }));
  const setOpenAttivita = (next) =>
    setCollapsedPrefs((prev) => ({
      ...prev,
      attivita: typeof next === "function" ? next(prev.attivita !== false) : next,
    }));
  const setOpenArchivio = (next) =>
    setCollapsedPrefs((prev) => ({
      ...prev,
      archivio: typeof next === "function" ? next(prev.archivio !== false) : next,
    }));

  // TIME Watch data (already cloud-synced via useTimeWatchData)
  const {
    totalEuro,
    dob,
    tempoDato,
    tempoAvuto,
    transactions,
    entrateTotali,
    speseTotali,
    accounts,
  } = useTimeWatchData();

  const { total: improntaTotal } = useImpronte();
  const { items: istanti } = useIstanti();

  // Snapshot builder for monthly backup — memoized to avoid re-runs.
  // Includes `accounts` so the monthly archive PDF can render account labels
  // for Entrate / Uscite / Switch (Sezione 5).
  const buildSnapshot = useCallback(
    () => ({
      totalEuro,
      dob,
      tempoDato,
      tempoAvuto,
      entrateTotali,
      speseTotali,
      transactions_count: transactions.length,
      transactions,
      accounts,
      created_at: new Date().toISOString(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const { backups } = useBackups(buildSnapshot);

  // Password change
  const [pwOpen, setPwOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");

  // Name edit
  const [nameEdit, setNameEdit] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameBusy, setNameBusy] = useState(false);
  const [nameErr, setNameErr] = useState("");
  const [nameMsg, setNameMsg] = useState("");

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwErr("");
    setPwMsg("");
    if (next.length < 8) {
      setPwErr("La nuova password deve avere almeno 8 caratteri.");
      return;
    }
    setPwBusy(true);
    try {
      await changePassword(current, next);
      setPwMsg("Password aggiornata con successo.");
      setCurrent("");
      setNext("");
    } catch (e2) {
      setPwErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setPwBusy(false);
    }
  };

  const handleSaveName = async (e) => {
    e.preventDefault();
    setNameErr("");
    setNameMsg("");
    const clean = nameDraft.trim();
    if (!clean) return setNameErr("Il nome non può essere vuoto.");
    if (clean.length > 80) return setNameErr("Massimo 80 caratteri.");
    setNameBusy(true);
    try {
      await updateProfile(clean);
      setNameMsg("Nome aggiornato.");
      setNameEdit(false);
    } catch (e2) {
      setNameErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setNameBusy(false);
    }
  };

  // === Derived time metrics ===
  // Fonti logiche DISTINTE, come nel TIME Watch:
  //   • "Tempo per lasciare il Segno" → dal DOB al centenario (y/m/d calendariale).
  //   • "Tempo Libero" → conversione del DENARO TOTALE in tempo (1€ = 1 minuto),
  //     esattamente la funzione `convertEuroToTime` usata dalla card "Libertà"
  //     del TIME Watch (page-04). NON deriva dal DOB.
  const [tickNow, setTickNow] = useState(0);
  useEffect(() => {
    // Tick per far transizionare a mezzanotte il contatore giorni della card
    // "Tempo per lasciare il Segno" (calendariale, date-only).
    const id = setInterval(() => setTickNow((v) => v + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  const segnoYmd = useMemo(
    () => pmvYearsMonthsDaysUntilCentenary(dob),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dob, tickNow]
  );
  // Fonte ufficiale identica al TIME Watch page-04:
  //   const time = useMemo(() => convertEuroToTime(euro), [euro]);
  const liberoParts = useMemo(
    () => convertEuroToTime(totalEuro || 0),
    [totalEuro]
  );
  const datoParts = useMemo(() => minutesToTimeParts(tempoDato), [tempoDato]);
  const avutoParts = useMemo(() => minutesToTimeParts(tempoAvuto), [tempoAvuto]);

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 max-w-5xl mx-auto" data-testid="area-personale">
      <PageHeader
        eyebrow="Area personale"
        title={user?.name ? `Ciao, ${user.name}` : "Il tuo profilo"}
        description="Il tuo account TIME Inside. I dati sono associati a te, sincronizzati nel cloud e salvati in modo permanente."
        testId="area-header"
      />

      {/* Shortcut verso il TIME Watch, subito sotto il testo introduttivo. */}
      <div className="-mt-8 mb-10 fade-in">
        <Link
          to="/innovazione/time-watch"
          data-testid="go-to-timewatch"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-emerald-300 hover:text-emerald-200 transition-colors"
        >
          Vai al TIME Watch →
        </Link>
      </div>

      {/* === Attività TIME Watch — sezione unica ordinata === */}
      <div className="mt-8 glass-card p-6" data-testid="attivita-timewatch">
        <button
          type="button"
          onClick={() => setOpenAttivita((v) => !v)}
          data-testid="attivita-toggle"
          aria-expanded={openAttivita}
          aria-controls="attivita-body"
          className="w-full flex items-center gap-3 mb-5 text-left hover:opacity-90 transition-opacity"
        >
          <div className="w-10 h-10 rounded-xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-amber-300" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-white/40">
              Attività TIME Watch
              <ChevronDown
                className={`w-3 h-3 text-white/40 transition-transform duration-300 motion-reduce:transition-none ${
                  openAttivita ? "rotate-0" : "-rotate-90"
                }`}
                aria-hidden="true"
              />
            </div>
            <div className="text-lg font-light text-white">
              Il tuo Tempo, momento per momento
            </div>
          </div>
        </button>
        <div
          id="attivita-body"
          data-testid="attivita-body"
          className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none ${
            openAttivita ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
          aria-hidden={!openAttivita}
        >
          <div className="overflow-hidden min-h-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <MetricCard
            icon={Clock}
            title="Tempo per Lasciare il Segno"
            testId="metric-segno"
            content={
              <>
                <TimeGroups
                  parts={{
                    years: segnoYmd?.years || 0,
                    months: segnoYmd?.months || 0,
                    days: segnoYmd?.days || 0,
                    hours: 0,
                    minutes: 0,
                  }}
                  units={["y", "m", "d"]}
                />
                {!segnoYmd && (
                  <div className="mt-3 text-[10px] tracking-[0.22em] uppercase text-white/30">
                    Imposta la data di nascita nel TIME Watch
                  </div>
                )}
              </>
            }
          />
          <MetricCard
            icon={Wallet}
            title="Denaro Totale"
            testId="metric-denaro"
            content={
              <div className="flex items-baseline gap-2" data-testid="metric-denaro-value">
                <span className="text-2xl sm:text-3xl font-extralight text-white tabular-nums">
                  {formatInt(totalEuro)}
                </span>
                <span className="text-[11px] tracking-[0.24em] uppercase text-white/40">€</span>
              </div>
            }
          />
          <MetricCard
            icon={Sparkles}
            title="Tempo Libero"
            testId="metric-libero"
            content={
              <TimeGroups parts={liberoParts} units={["y", "m", "d", "h", "min"]} />
            }
          />
          <MetricCard
            icon={Gift}
            title="Tempo Offerto"
            testId="metric-offerto"
            content={<TimeGroups parts={datoParts} units={["y", "m", "d", "h", "min"]} />}
          />
          <MetricCard
            icon={Heart}
            title="Tempo Ricevuto"
            testId="metric-ricevuto"
            content={<TimeGroups parts={avutoParts} units={["y", "m", "d", "h", "min"]} />}
          />
          <MetricCard
            icon={Fingerprint}
            title="Impronte"
            testId="metric-impronte"
            content={
              <div className="flex items-baseline gap-2" data-testid="metric-impronte-value">
                <span className="text-2xl sm:text-3xl font-extralight text-white tabular-nums">
                  {formatInt(improntaTotal)}
                </span>
                <span className="text-[11px] tracking-[0.24em] uppercase text-white/40">
                  {improntaTotal === 1 ? "impronta" : "impronte"}
                </span>
              </div>
            }
          />
          <MetricCard
            icon={Camera}
            title="Istanti nel Cloud"
            testId="metric-istanti"
            content={
              <div className="flex items-baseline gap-2" data-testid="metric-istanti-value">
                <span className="text-2xl sm:text-3xl font-extralight text-white tabular-nums">
                  {formatInt(istanti.length)}
                </span>
                <span className="text-[11px] tracking-[0.24em] uppercase text-white/40">
                  {istanti.length === 1 ? "foto" : "foto"}
                </span>
              </div>
            }
          />
        </div>
          </div>
        </div>
      </div>

      {/* === Archivio del Tempo (backups) === */}
      <div className="mt-8 glass-card p-6" data-testid="archivio-card">
        <button
          type="button"
          onClick={() => setOpenArchivio((v) => !v)}
          data-testid="archivio-toggle"
          aria-expanded={openArchivio}
          aria-controls="archivio-body"
          className="w-full flex items-center gap-3 mb-5 text-left hover:opacity-90 transition-opacity"
        >
          <div className="w-10 h-10 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center flex-shrink-0">
            <Archive className="w-5 h-5 text-white/70" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-white/40">
              Archivio del Tempo
              <ChevronDown
                className={`w-3 h-3 text-white/40 transition-transform duration-300 motion-reduce:transition-none ${
                  openArchivio ? "rotate-0" : "-rotate-90"
                }`}
                aria-hidden="true"
              />
            </div>
            <div className="text-lg font-light text-white">
              La tua memoria mensile, scaricabile
            </div>
          </div>
        </button>
        <div
          id="archivio-body"
          data-testid="archivio-body"
          className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none ${
            openArchivio ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
          aria-hidden={!openArchivio}
        >
          <div className="overflow-hidden min-h-0">
        {backups.length === 0 ? (
          <p className="text-sm text-white/45 font-light leading-relaxed" data-testid="archivio-empty">
            Il primo Archivio verrà creato automaticamente questo mese. Ogni
            mese l&apos;app raccoglie la tua Attività TIME Watch, la
            distribuzione delle Impronte, il diario delle Impronte e i tuoi
            Istanti in un unico file scaricabile.
          </p>
        ) : (
          <ul
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="archivio-list"
          >
            {backups.map((b) => (
              <ArchiveItem key={b.period} period={b.period} />
            ))}
          </ul>
        )}
          </div>
        </div>
      </div>

      {/* === Documenti Legali — accettazioni NDA === */}
      <NdaAcceptancesSection />

      {/* === Account — sezione in fondo === */}
      <div className="mt-8 glass-card p-6" data-testid="account-card">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center flex-shrink-0">
              <UserIcon className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => setOpenAccount((v) => !v)}
                data-testid="account-toggle"
                aria-expanded={openAccount}
                aria-controls="account-body"
                className="flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-white/40 hover:text-white/60 transition-colors"
              >
                Account
                <ChevronDown
                  className={`w-3 h-3 text-white/40 transition-transform duration-300 motion-reduce:transition-none ${
                    openAccount ? "rotate-0" : "-rotate-90"
                  }`}
                  aria-hidden="true"
                />
              </button>
              {nameEdit ? (
                <form onSubmit={handleSaveName} data-testid="edit-name-form" className="mt-1 flex items-center gap-2">
                  <input
                    autoFocus
                    data-testid="edit-name-input"
                    type="text"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    maxLength={80}
                    className="min-w-0 flex-1 px-3 py-1.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white placeholder-white/25 focus:border-emerald-400/40 focus:outline-none"
                    placeholder="Il tuo nome"
                  />
                  <button
                    type="submit"
                    disabled={nameBusy}
                    data-testid="edit-name-save"
                    className="p-1.5 rounded-lg bg-emerald-400/90 text-black hover:bg-emerald-400 transition-colors disabled:opacity-50"
                    aria-label="Salva nome"
                  >
                    {nameBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNameEdit(false);
                      setNameErr("");
                    }}
                    data-testid="edit-name-cancel"
                    className="p-1.5 rounded-lg border border-white/15 text-white/60 hover:text-white hover:border-white/30 transition-colors"
                    aria-label="Annulla"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </form>
              ) : (
                <div className="flex items-center gap-2">
                  <div data-testid="account-name" className="text-lg font-light text-white truncate">
                    {user?.name || "Utente TIME Inside"}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setNameDraft(user?.name || "");
                      setNameEdit(true);
                      setNameErr("");
                      setNameMsg("");
                      setOpenAccount(true);
                    }}
                    data-testid="edit-name-toggle"
                    className="p-1 rounded text-white/40 hover:text-white/80 transition-colors"
                    aria-label="Modifica nome"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>
          <span
            data-testid="account-role"
            className="text-[10px] tracking-[0.24em] uppercase px-3 py-1 rounded-full border border-white/10 text-white/50 flex-shrink-0"
          >
            {user?.role === "admin" ? "Admin" : "Utente"}
          </span>
        </div>

        <div
          id="account-body"
          data-testid="account-body"
          className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none ${
            openAccount ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
          aria-hidden={!openAccount}
        >
          <div className="overflow-hidden min-h-0">
            {nameErr && (
              <div
                data-testid="edit-name-error"
                className="mb-4 flex items-center gap-2 text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-3 py-2"
              >
                <AlertCircle className="w-3.5 h-3.5" />
                {nameErr}
              </div>
            )}
            {nameMsg && (
              <div
                data-testid="edit-name-success"
                className="mb-4 flex items-center gap-2 text-[12px] text-emerald-300 bg-emerald-500/[0.08] border border-emerald-500/20 rounded-lg px-3 py-2"
              >
                <Check className="w-3.5 h-3.5" />
                {nameMsg}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <InfoRow icon={Mail} label="Email" value={user?.email} testId="account-email" />
              <InfoRow icon={Calendar} label="Iscritto dal" value={formatDate(user?.created_at)} testId="account-created" />
              <InfoRow icon={ShieldCheck} label="Sessione" value="Sicura · cookie httpOnly" testId="account-session" />
              <InfoRow icon={Watch} label="Sync" value="Cloud MongoDB + backup mensile" testId="account-sync" />
            </div>

            <div className="flex flex-wrap gap-3 mt-8">
              <button
                onClick={() => setPwOpen((v) => !v)}
                data-testid="toggle-change-password"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-[11px] tracking-[0.24em] uppercase text-white/70 hover:text-white hover:border-white/30 transition-all"
              >
                <KeyRound className="w-3.5 h-3.5" />
                Cambia password
              </button>
              <button
                onClick={() => {
                  // Riapre volontariamente HomeOnboardingDialog senza toccare
                  // homeOnboardingDismissed. Il flag ephemero in sessionStorage
                  // fa in modo che il popup appaia UNA sola volta al prossimo
                  // approdo sulla Home; i login successivi non ricompaiono.
                  try {
                    sessionStorage.setItem("timeInside:onboardingForceOpen", "1");
                  } catch {
                    /* no-op */
                  }
                  navigate("/");
                }}
                data-testid="review-onboarding-button"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-400/25 text-[11px] tracking-[0.24em] uppercase text-emerald-300/80 hover:bg-emerald-500/5 hover:border-emerald-400/40 hover:text-emerald-200 transition-all"
              >
                <Compass className="w-3.5 h-3.5" />
                Rivedi onboarding
              </button>
              <button
                onClick={() => setDeleteOpen(true)}
                data-testid="open-delete-account"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-400/25 text-[11px] tracking-[0.24em] uppercase text-red-300/80 hover:bg-red-500/5 hover:border-red-400/40 hover:text-red-200 transition-all"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Elimina account
              </button>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-400/30 text-[11px] tracking-[0.24em] uppercase text-red-300 hover:bg-red-500/10 hover:border-red-400/50 transition-all"
              >
                <LogOut className="w-3.5 h-3.5" />
                Esci
              </button>
            </div>

            {pwOpen && (
              <form onSubmit={handleChangePassword} className="mt-8 pt-8 border-t border-white/5 space-y-4" data-testid="change-password-form">
                <div className="text-[11px] tracking-[0.24em] uppercase text-white/40">
                  Cambia password
                </div>
                <PwField testId="cp-current" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Password attuale" minLength={1} />
                <PwField testId="cp-new" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="Nuova password (min 8 caratteri)" minLength={8} />
                {pwErr && (
                  <div data-testid="cp-error" className="flex items-center gap-2 text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-3 py-2">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {pwErr}
                  </div>
                )}
                {pwMsg && (
                  <div data-testid="cp-success" className="flex items-center gap-2 text-[12px] text-emerald-300 bg-emerald-500/[0.08] border border-emerald-500/20 rounded-lg px-3 py-2">
                    <Check className="w-3.5 h-3.5" />
                    {pwMsg}
                  </div>
                )}
                <button
                  data-testid="cp-submit"
                  type="submit"
                  disabled={pwBusy}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-400/90 text-black text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-emerald-400 transition-all disabled:opacity-50"
                >
                  {pwBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Aggiorna password
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {deleteOpen && (
        <DeleteAccountDialog
          onClose={() => setDeleteOpen(false)}
          onDeleted={async () => {
            // Il backend ha già invalidato la sessione (clear cookies).
            // Pulizia locale multi-account: rimuovi TUTTE le chiavi user-scoped
            // relative all'utente appena eliminato + logout dello stato React.
            try {
              const uid = user?.id;
              if (uid) {
                const toRemove = [];
                for (let i = 0; i < window.localStorage.length; i += 1) {
                  const k = window.localStorage.key(i);
                  if (!k) continue;
                  if (
                    k === `time-watch:data:user:${uid}` ||
                    k === `time-watch:impronte:user:${uid}` ||
                    k.startsWith(`time-inside:pref:user:${uid}:`)
                  ) {
                    toRemove.push(k);
                  }
                }
                toRemove.forEach((k) => {
                  try { window.localStorage.removeItem(k); } catch { /* ignore */ }
                });
              }
            } catch { /* ignore */ }
            await logout();
            navigate("/", { replace: true });
          }}
        />
      )}
    </div>
  );
}

/**
 * ArchiveItem — singola cartella mensile scaricabile.
 * Il download passa da un fetch autenticato (cookie httpOnly) verso
 * `/api/backups/{period}/download`, che restituisce uno ZIP contenente:
 *   • Archivio-{Mese-Anno}.pdf (4 sezioni)
 *   • Istanti/*.jpg|png (foto del mese)
 * Ogni utente può scaricare esclusivamente il proprio archivio.
 */
function ArchiveItem({ period }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const handleDownload = async () => {
    setErr("");
    setBusy(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/backups/${period}/download`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        throw new Error("Download non riuscito");
      }
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `Archivio del Tempo - ${periodLabel(period)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // rilascia la memoria dopo un breve delay per lasciar partire il download
      setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
    } catch (e) {
      setErr(e.message || "Errore nel download");
    } finally {
      setBusy(false);
    }
  };
  return (
    <li
      data-testid={`archivio-item-${period}`}
      className="p-4 rounded-xl bg-black/30 border border-white/5 flex items-center justify-between gap-3"
    >
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.24em] uppercase text-white/35 mb-1">
          {period}
        </div>
        <div className="text-sm text-white/80 font-light truncate">
          {periodLabel(period)}
        </div>
        {err && (
          <div
            className="mt-1 text-[10px] text-red-300 font-light"
            data-testid={`archivio-error-${period}`}
          >
            {err}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleDownload}
        disabled={busy}
        data-testid={`archivio-download-${period}`}
        aria-label={`Scarica l'Archivio del Tempo di ${periodLabel(period)}`}
        className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-full border border-emerald-400/40 text-[10px] tracking-[0.22em] uppercase text-emerald-300 hover:bg-emerald-400/10 hover:border-emerald-400/60 hover:text-emerald-200 transition-all disabled:opacity-50 disabled:cursor-wait"
      >
        {busy ? (
          <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
        <span>Scarica</span>
      </button>
    </li>
  );
}

function InfoRow({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="flex items-start gap-3 p-4 rounded-xl bg-black/30 border border-white/5">
      <Icon className="w-4 h-4 text-white/40 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.24em] uppercase text-white/35 mb-1">{label}</div>
        <div className="text-sm text-white/85 font-light break-all">{value || "—"}</div>
      </div>
    </div>
  );
}

function PwField({ testId, value, onChange, autoComplete, placeholder, minLength }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        data-testid={testId}
        type={visible ? "text" : "password"}
        autoComplete={autoComplete}
        required
        minLength={minLength}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-4 pr-11 py-3 rounded-xl bg-black/40 border border-white/10 text-sm text-white placeholder-white/25 focus:border-emerald-400/40 focus:outline-none"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        data-testid={`${testId}-toggle`}
        aria-label={visible ? "Nascondi password" : "Mostra password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function MetricCard({ icon: Icon, title, content, testId }) {
  return (
    <div
      data-testid={testId}
      className="glass-card p-5"
    >
      <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-white/40 mb-4">
        <Icon className="w-3.5 h-3.5" />
        <span data-testid={`${testId}-title`}>{title}</span>
      </div>
      <div>{content}</div>
    </div>
  );
}

/**
 * TimeGroups — render Y / M / D / H / min in "Title above / Value below" style,
 * responsive: on mobile stacks nicely, wraps on very small screens.
 */
function TimeGroups({ parts, units }) {
  const UNIT_MAP = {
    y: { label: "Anni", val: parts.years },
    m: { label: "Mesi", val: parts.months },
    d: { label: "Giorni", val: parts.days },
    h: { label: "Ore", val: parts.hours },
    min: { label: "Minuti", val: parts.minutes },
  };
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-3">
      {units.map((u) => {
        const item = UNIT_MAP[u];
        if (!item) return null;
        return (
          <div key={u} data-testid={`unit-${u}`} className="flex flex-col">
            <span className="text-[9px] tracking-[0.24em] uppercase text-white/35">
              {item.label}
            </span>
            <span className="text-2xl sm:text-3xl font-extralight text-white tabular-nums leading-tight">
              {formatInt(item.val)}
            </span>
          </div>
        );
      })}
    </div>
  );
}


/**
 * DeleteAccountDialog — modale di eliminazione account.
 *
 * Flusso in DUE PASSAGGI:
 *   Step 1 → conferma iniziale ("Continua con l'eliminazione" / "Annulla").
 *   Step 2 → password corrente + digitazione della parola "ELIMINA".
 *
 * L'azione è irreversibile. Al successo del backend, viene invocato
 * `onDeleted()` per gestire la pulizia locale + logout + redirect.
 */
function DeleteAccountDialog({ onClose, onDeleted }) {
  const [step, setStep] = useState(1); // 1 = intro, 2 = strong-confirm
  const [password, setPassword] = useState("");
  const [confirmWord, setConfirmWord] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const proceed = () => {
    setErr("");
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (confirmWord.trim().toUpperCase() !== "ELIMINA") {
      setErr('Digita la parola "ELIMINA" per confermare');
      return;
    }
    if (!password) {
      setErr("Inserisci la password");
      return;
    }
    setBusy(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/account`;
      const res = await fetch(url, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, confirm: confirmWord.trim().toUpperCase() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(formatApiErrorDetail(body?.detail) || "Errore nell'eliminazione");
        setBusy(false);
        return;
      }
      await onDeleted?.();
    } catch (e2) {
      setErr(e2.message || "Errore nell'eliminazione");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="delete-account-dialog"
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={busy ? undefined : onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="glass-card w-full max-w-md p-7 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-red-300/90 mb-3">
          <Trash2 className="w-3 h-3" />
          Azione irreversibile
        </div>
        <h2 className="text-2xl sm:text-[26px] font-extralight text-white leading-tight mb-4">
          Eliminare definitivamente il tuo account?
        </h2>
        <p className="text-sm text-white/65 font-light leading-relaxed mb-6">
          Questa operazione eliminerà il tuo account e i dati personali
          associati (Impronte, Istanti, TIME Watch, preferenze, archivio
          mensile). Non potrà essere annullata.
        </p>

        {step === 1 && (
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onClose}
              data-testid="delete-account-cancel"
              className="px-5 py-2.5 rounded-full border border-white/15 text-[11px] tracking-[0.24em] uppercase text-white/70 hover:text-white hover:border-white/30 transition-all"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={proceed}
              data-testid="delete-account-continue"
              className="px-5 py-2.5 rounded-full bg-red-500/15 border border-red-400/40 text-[11px] tracking-[0.24em] uppercase text-red-200 hover:bg-red-500/25 hover:border-red-400/60 transition-all"
            >
              Continua con l&apos;eliminazione
            </button>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label
                htmlFor="delete-acc-pw"
                className="block text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2"
              >
                Password corrente
              </label>
              <div className="relative">
                <input
                  id="delete-acc-pw"
                  data-testid="delete-account-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white/90 focus:border-red-400/40 focus:outline-none font-light"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-white/40 hover:text-white"
                  aria-label={showPw ? "Nascondi password" : "Mostra password"}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label
                htmlFor="delete-acc-word"
                className="block text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2"
              >
                Digita <span className="text-red-300">ELIMINA</span> per confermare
              </label>
              <input
                id="delete-acc-word"
                data-testid="delete-account-confirm-word"
                type="text"
                value={confirmWord}
                onChange={(e) => setConfirmWord(e.target.value)}
                placeholder="ELIMINA"
                autoCapitalize="characters"
                className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white/90 focus:border-red-400/40 focus:outline-none font-mono tracking-[0.24em]"
              />
            </div>
            {err && (
              <div
                data-testid="delete-account-error"
                className="text-sm text-red-300 font-light"
              >
                {err}
              </div>
            )}
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                data-testid="delete-account-cancel-step2"
                className="px-5 py-2.5 rounded-full border border-white/15 text-[11px] tracking-[0.24em] uppercase text-white/70 hover:text-white hover:border-white/30 transition-all disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="delete-account-confirm"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-500/80 hover:bg-red-500 text-white text-[11px] tracking-[0.24em] uppercase font-medium transition-all disabled:opacity-50 disabled:cursor-wait"
              >
                {busy ? (
                  <Loader2Icon className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Elimina definitivamente
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
